import { ChevronRight } from 'lucide-react';
import { createContext, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';

/** Opening a section tells the others on the page, so only one is open at a time (#406). */
const OPENED = 'cg-collapsible-opened';

/**
 * A settings section that collapses, so long settings pages stay short and scannable. Every section starts closed
 * (#406), except one the URL hash names (a link straight to it). Opening one closes whichever other section is open,
 * apart from a section it sits inside.
 */
export function Collapsible({
  id,
  title,
  icon,
  anchors = [],
  children,
}: {
  id: string;
  title: ReactNode;
  icon?: ReactNode;
  /** Ids of folds inside it (#601): a link to one of them opens this section too. */
  anchors?: string[];
  children: ReactNode;
}) {
  // Open/closed lives only while the page is shown: leaving and coming back starts closed again (#396).
  const [open, setOpen] = useState(() => location.hash === `#${id}` || anchors.some((a) => location.hash === `#${a}`));
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const onOpened = (e: Event) => {
      const opened = (e as CustomEvent<HTMLElement>).detail;
      // Close, unless it's this section or one inside it.
      if (opened !== ref.current && !ref.current?.contains(opened)) setOpen(false);
    };
    window.addEventListener(OPENED, onOpened);
    return () => window.removeEventListener(OPENED, onOpened);
  }, []);
  function toggle() {
    const next = !open;
    setOpen(next);
    if (next && ref.current) window.dispatchEvent(new CustomEvent(OPENED, { detail: ref.current }));
  }
  return (
    <section ref={ref} id={id} className={`card stack collapsible ${open ? 'open' : ''}`}>
      <button className="collapsible-head" aria-expanded={open} onClick={toggle}>
        <ChevronRight size={18} className={`chevron ${open ? 'open' : ''}`} />
        <span className="collapsible-title">
          {icon}
          {title}
        </span>
      </button>
      {open && (
        <div className="collapsible-body stack">
          <FoldGroup>{children}</FoldGroup>
        </div>
      )}
    </section>
  );
}

/**
 * A settings section: a Collapsible card on its own, or, when `embedded` inside another card (e.g. Settings → School
 * Settings, #317), a fold inside it (#601).
 */
export function Section({
  embedded = false,
  id,
  title,
  icon,
  summary,
  children,
}: {
  embedded?: boolean;
  id: string;
  title: ReactNode;
  icon?: ReactNode;
  summary?: ReactNode;
  children: ReactNode;
}) {
  if (!embedded) return <Collapsible id={id} title={title} icon={icon}>{children}</Collapsible>;
  return (
    <Fold id={id} title={title} summary={summary}>
      {children}
    </Fold>
  );
}

// --- Folds (#601) ----------------------------------------------------------------------------------------------------

/** The folds in one section (or one fold): one open at a time, like the sections themselves. */
const FoldContext = createContext<{ open: string; setOpen: (key: string) => void } | null>(null);

export function FoldGroup({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState('');
  return <FoldContext.Provider value={{ open, setOpen }}>{children}</FoldContext.Provider>;
}

/**
 * A setting with more settings under it, folded to one row inside its section (#601): its name and, on the right, a
 * short summary of how it's set ("On · 50% off for life"). Opening one closes the other folds in the same section.
 * What's inside stays mounted while folded, so a half-typed value isn't lost. A link to its id opens it.
 */
export function Fold({ id, title, summary, icon, children }: { id?: string; title: ReactNode; summary?: ReactNode; icon?: ReactNode; children: ReactNode }) {
  const group = useContext(FoldContext);
  const auto = useId();
  const key = id ?? auto;
  const [solo, setSolo] = useState(() => !!id && location.hash === `#${id}`);
  const open = group ? group.open === key : solo;
  useEffect(() => {
    if (group && id && location.hash === `#${id}`) group.setOpen(key);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = () => (group ? group.setOpen(open ? '' : key) : setSolo(!open));
  return (
    <div id={id} className={`fold ${open ? 'open' : ''}`}>
      <button type="button" className="fold-head" aria-expanded={open} onClick={toggle}>
        <ChevronRight size={16} className={`chevron ${open ? 'open' : ''}`} aria-hidden />
        <span className="fold-title">
          {icon}
          {title}
        </span>
        {summary !== undefined && summary !== null && summary !== '' && <span className="fold-summary">{summary}</span>}
      </button>
      <div className="fold-body stack" hidden={!open}>
        <FoldGroup>{children}</FoldGroup>
      </div>
    </div>
  );
}
